import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import cn from "classnames";
import styles from "./ExpeditorGuidePage.module.scss";
import { useWindowSize } from "../../../hooks/useWindowSize";
import LogoIcon from "../../../components/shared/icons/LogoIcon";
import { LanguageDropdown } from "../../../components/LanguageSelector/LanguageSelector";

const ExpeditorGuidePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();

  const [activeSection, setActiveSection] = useState("about");
  const [activeMode, setActiveMode] = useState("auto");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const showSection = (sectionId, scrollTarget) => {
    setActiveSection(sectionId);
    window.scrollTo(0, 0);
    setSidebarOpen(false);
    if (scrollTarget) {
      setTimeout(() => {
        const el = document.getElementById(`${sectionId}-${scrollTarget}`);
        if (el) {
          const headerOffset = 80;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  };

  const switchMode = (mode) => {
    setActiveMode(mode);
    showSection(mode === "auto" ? "auto-execute" : "avia-execute");
  };

  const sectionClass = (id) => `${styles.pageSection} ${activeSection === id ? styles.visible : ""}`;

  const navLinkClass = (sectionId, isSub = false) => {
    let cls = styles.navLink;
    if (isSub) cls += ` ${styles.navSub}`;
    if (activeSection === sectionId && !isSub) cls += ` ${styles.active}`;
    return cls;
  };

  return (
    <div className={styles.guideWrapper}>
      {/* HEADER */}
      <header className={styles.header}>
        <div className={styles.headerLogo} onClick={() => navigate("/main")} style={{ cursor: "pointer" }}>
          <LogoIcon />
        </div>
        <div className={styles.headerSep}></div>
        {!isMobile && <div className={styles.headerTitle}>{t("expeditorGuidePage.headerTitle")}</div>}
        <div className={"ms-auto"}>
          <LanguageDropdown />
        </div>
        <button className={styles.mobileToggle} onClick={() => setSidebarOpen(!sidebarOpen)}>
          ☰
        </button>
      </header>

      {/* SIDEBAR */}
      <nav className={`${styles.sidebar} ${sidebarOpen ? styles.open : ""}`}>
        <div className={styles.navGroup}>
          <div className={styles.navGroupLabel}>{t("expeditorGuidePage.nav.general")}</div>
          <a className={navLinkClass("about")} onClick={() => showSection("about")}>
            <span className={styles.navIcon}>1</span> {t("expeditorGuidePage.nav.about")}
          </a>
          <a className={navLinkClass("differ")} onClick={() => showSection("differ")}>
            <span className={styles.navIcon}>2</span> {t("expeditorGuidePage.nav.differ")}
          </a>
          <a className={navLinkClass("verification")} onClick={() => showSection("verification")}>
            <span className={styles.navIcon}>3</span> {t("expeditorGuidePage.nav.verification")}
          </a>
        </div>

        <div className={styles.navDivider}></div>

        <div className={styles.navGroup}>
          <div className={styles.navGroupLabel}>{t("expeditorGuidePage.nav.sections")}</div>
          <div className={styles.navTabSwitch}>
            <button
              className={`${styles.navTabBtn} ${activeMode === "auto" ? styles.active : ""}`}
              onClick={() => switchMode("auto")}
            >
              {t("expeditorGuidePage.nav.auto")}
            </button>
            <button
              className={`${styles.navTabBtn} ${activeMode === "avia" ? styles.active : ""}`}
              onClick={() => switchMode("avia")}
            >
              {t("expeditorGuidePage.nav.avia")}
            </button>
          </div>
        </div>

        {/* AUTO NAV */}
        {activeMode === "auto" && (
          <div className={styles.navGroup}>
            <a className={navLinkClass("auto-execute")} onClick={() => showSection("auto-execute")}>
              <span className={styles.navIcon}>01</span> {t("expeditorGuidePage.nav.executeOrders")}
            </a>
            <a className={navLinkClass("auto-execute", true)} onClick={() => showSection("auto-execute", "tender")}>
              {t("expeditorGuidePage.nav.tenderFromPartner")}
            </a>
            <a className={navLinkClass("auto-execute", true)} onClick={() => showSection("auto-execute", "direct")}>
              {t("expeditorGuidePage.nav.directAssignment")}
            </a>
            {/* <a className={navLinkClass("auto-execute", true)} onClick={() => showSection("auto-execute", "public")}>
              {t("expeditorGuidePage.nav.publicOrders")}
            </a> */}
            <a className={navLinkClass("auto-create")} onClick={() => showSection("auto-create")}>
              <span className={styles.navIcon}>02</span> {t("expeditorGuidePage.nav.createOrder")}
            </a>
            <a className={navLinkClass("auto-create", true)} onClick={() => showSection("auto-create", "own-fleet")}>
              {t("expeditorGuidePage.nav.ownFleet")}
            </a>
            <a className={navLinkClass("auto-create", true)} onClick={() => showSection("auto-create", "public")}>
              {t("expeditorGuidePage.nav.publicMarket")}
            </a>
            <a className={navLinkClass("auto-order")} onClick={() => showSection("auto-order")}>
              <span className={styles.navIcon}>03</span> {t("expeditorGuidePage.nav.orderSection")}
            </a>
            <a className={navLinkClass("auto-order", true)} onClick={() => showSection("auto-order", "tabs")}>
              {t("expeditorGuidePage.nav.orderTabs")}
            </a>
            <a className={navLinkClass("auto-order", true)} onClick={() => showSection("auto-order", "details")}>
              {t("expeditorGuidePage.nav.orderDetails")}
            </a>
            <a className={navLinkClass("auto-order", true)} onClick={() => showSection("auto-order", "filter")}>
              {t("expeditorGuidePage.nav.filterExport")}
            </a>
            <a className={navLinkClass("auto-order", true)} onClick={() => showSection("auto-order", "statuses")}>
              {t("expeditorGuidePage.nav.orderStatuses")}
            </a>
            <a className={navLinkClass("auto-order", true)} onClick={() => showSection("auto-order", "history")}>
              {t("expeditorGuidePage.nav.orderHistory")}
            </a>
            <a className={navLinkClass("auto-fleet")} onClick={() => showSection("auto-fleet")}>
              <span className={styles.navIcon}>04</span> {t("expeditorGuidePage.nav.fleet")}
            </a>
            <a className={navLinkClass("auto-drivers")} onClick={() => showSection("auto-drivers")}>
              <span className={styles.navIcon}>05</span> {t("expeditorGuidePage.nav.drivers")}
            </a>
            <a className={navLinkClass("auto-marketplace")} onClick={() => showSection("auto-marketplace")}>
              <span className={styles.navIcon}>06</span> {t("expeditorGuidePage.nav.marketplace")}
            </a>
            <a className={navLinkClass("auto-analytics")} onClick={() => showSection("auto-analytics")}>
              <span className={styles.navIcon}>07</span> {t("expeditorGuidePage.nav.analytics")}
            </a>
            <a className={navLinkClass("auto-staff")} onClick={() => showSection("auto-staff")}>
              <span className={styles.navIcon}>08</span> {t("expeditorGuidePage.nav.staff")}
            </a>
          </div>
        )}

        {/* AVIA NAV */}
        {activeMode === "avia" && (
          <div className={styles.navGroup}>
            <a className={navLinkClass("avia-execute")} onClick={() => showSection("avia-execute")}>
              <span className={styles.navIcon}>01</span> {t("expeditorGuidePage.nav.executeOrders")}
            </a>
            <a className={navLinkClass("avia-execute", true)} onClick={() => showSection("avia-execute", "tender")}>
              {t("expeditorGuidePage.nav.tenderFromPartner")}
            </a>
            <a className={navLinkClass("avia-execute", true)} onClick={() => showSection("avia-execute", "direct")}>
              {t("expeditorGuidePage.nav.directAssignment")}
            </a>
            {/* <a className={navLinkClass("avia-execute", true)} onClick={() => showSection("avia-execute", "public")}>
              {t("expeditorGuidePage.nav.publicOrders")}
            </a> */}
            <a className={navLinkClass("avia-order")} onClick={() => showSection("avia-order")}>
              <span className={styles.navIcon}>02</span> {t("expeditorGuidePage.nav.orderSection")}
            </a>
            <a className={navLinkClass("avia-order", true)} onClick={() => showSection("avia-order", "statuses")}>
              {t("expeditorGuidePage.nav.orderStatuses")}
            </a>
            <a className={navLinkClass("avia-marketplace")} onClick={() => showSection("avia-marketplace")}>
              <span className={styles.navIcon}>03</span> {t("expeditorGuidePage.nav.marketplace")}
            </a>
            <a className={navLinkClass("avia-analytics")} onClick={() => showSection("avia-analytics")}>
              <span className={styles.navIcon}>04</span> {t("expeditorGuidePage.nav.analytics")}
            </a>
            <a className={navLinkClass("avia-staff")} onClick={() => showSection("avia-staff")}>
              <span className={styles.navIcon}>05</span> {t("expeditorGuidePage.nav.staff")}
            </a>
          </div>
        )}
      </nav>

      {/* MAIN CONTENT */}
      <main className={styles.main}>
        {/* ==================== ABOUT ==================== */}
        <div className={sectionClass("about")} id="about">
          {isMobile && <div className={cn(styles.headerTitle)}>{t("expeditorGuidePage.headerTitle")}</div>}
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.about.title")}</h1>
          <p className={styles.bodyText}>{t("expeditorGuidePage.about.description1")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.about.description2")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.about.helpsTitle")}</h3>
          {["help1", "help2", "help3", "help4"].map((key) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`expeditorGuidePage.about.${key}`)}</h4>
              </div>
            </div>
          ))}

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("expeditorGuidePage.about.callout")}</div>
          </div>
        </div>

        {/* ==================== DIFFER ==================== */}
        <div className={sectionClass("differ")} id="differ">
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.differ.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.differ.subtitle")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.differ.description1")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.differ.description2")}</p>

          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div className={styles.featureRow} key={i}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`expeditorGuidePage.differ.feature${i}Title`)}</h4>
                <p>{t(`expeditorGuidePage.differ.feature${i}Desc`)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ==================== VERIFICATION ==================== */}
        <div className={sectionClass("verification")} id="verification">
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.verification.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.verification.subtitle")}</p>
          <p className={styles.bodyText}>
            <strong>{t("expeditorGuidePage.verification.whyNeeded")}</strong>{" "}
            {t("expeditorGuidePage.verification.whyNeededDesc")}
          </p>

          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.verification.step${i}Title`)}</h4>
                  <p>{t(`expeditorGuidePage.verification.step${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("expeditorGuidePage.verification.callout")}</div>
          </div>
        </div>

        {/* ============================================================ */}
        {/* АВТО РАЗДЕЛЫ */}
        {/* ============================================================ */}

        {/* ==================== AUTO: ИСПОЛНЕНИЕ ЗАКАЗОВ ==================== */}
        <div className={sectionClass("auto-execute")} id="auto-execute">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("expeditorGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.autoExecute.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.autoExecute.subtitle")}</p>

          {/* ТЕНДЕР */}
          <h3 className={styles.sectionHeading} id="auto-execute-tender">{t("expeditorGuidePage.autoExecute.tenderTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoExecute.tenderDesc")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoExecute.tenderWhen")}</p>

          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.autoExecute.tenderStep${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.autoExecute.tenderStep${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ПРЯМОЕ НАЗНАЧЕНИЕ */}
          <h3 className={styles.sectionHeading} id="auto-execute-direct">{t("expeditorGuidePage.autoExecute.directTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoExecute.directDesc")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoExecute.directWhen")}</p>

          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.autoExecute.directStep${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.autoExecute.directStep${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("expeditorGuidePage.autoExecute.directCallout")}</div>
          </div>

          {/* ПУБЛИЧНЫЕ ЗАКАЗЫ */}
          {/* <h3 className={styles.sectionHeading} id="auto-execute-public">{t("expeditorGuidePage.autoExecute.publicTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoExecute.publicDesc")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoExecute.publicWhen")}</p>

          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.autoExecute.publicStep${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.autoExecute.publicStep${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`${styles.callout} ${styles.success}`}>
            <div className={styles.calloutIcon}>✓</div>
            <div>{t("expeditorGuidePage.autoExecute.publicCallout")}</div>
          </div> */}
        </div>

        {/* ==================== AUTO: СОЗДАНИЕ ЗАКАЗА ==================== */}
        <div className={sectionClass("auto-create")} id="auto-create">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("expeditorGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.autoCreate.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.autoCreate.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoCreate.fillTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.fillDesc")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.blockFrom")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.blockTo")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.blockDetails")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.blockPayment")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoCreate.choosePlacement")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.choosePlacementDesc")}</p>

          {/* Свой автопарк */}
          <h3 className={styles.sectionHeading} id="auto-create-own-fleet">{t("expeditorGuidePage.autoCreate.ownFleetTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.ownFleetDesc")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.ownFleetWhen")}</p>

          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.autoCreate.ownFleetStep${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.autoCreate.ownFleetStep${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Публичный рынок */}
          <h3 className={styles.sectionHeading} id="auto-create-public">{t("expeditorGuidePage.autoCreate.publicTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.publicDesc")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoCreate.publicWhen")}</p>

          <div className={styles.stepList}>
            {[1, 2].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.autoCreate.publicStep${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.autoCreate.publicStep${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("expeditorGuidePage.autoCreate.callout")}</div>
          </div>
        </div>

        {/* ==================== AUTO: РАЗДЕЛ ЗАКАЗЫ ==================== */}
        <div className={sectionClass("auto-order")} id="auto-order">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("expeditorGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.autoOrder.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.autoOrder.subtitle")}</p>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("expeditorGuidePage.autoOrder.marketplaceCallout")}</div>
          </div>

          {/* Вкладки */}
          <h3 className={styles.sectionHeading} id="auto-order-tabs">{t("expeditorGuidePage.autoOrder.tabsTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoOrder.tabsDesc")}</p>

          <div className={styles.cardsGrid} style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#DBEAFE" }}>📥</div>
              <h4>{t("expeditorGuidePage.autoOrder.tabAssignedTitle")}</h4>
              <p>{t("expeditorGuidePage.autoOrder.tabAssignedDesc")}</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#D1FAE5" }}>📤</div>
              <h4>{t("expeditorGuidePage.autoOrder.tabOwnTitle")}</h4>
              <p>{t("expeditorGuidePage.autoOrder.tabOwnDesc")}</p>
            </div>
          </div>

          {/* Детали заказа */}
          <h3 className={styles.sectionHeading} id="auto-order-details">{t("expeditorGuidePage.autoOrder.detailsTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoOrder.detailsDesc")}</p>
          <p className={styles.bodyText}><strong>{t("expeditorGuidePage.autoOrder.toolsTitle")}</strong></p>

          {["bidding", "assignDriverTransport", "chatClient", "viewStatuses", "copyOrder"].map((key) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`expeditorGuidePage.autoOrder.${key}`)}</h4>
                <p>{t(`expeditorGuidePage.autoOrder.${key}Desc`)}</p>
              </div>
            </div>
          ))}

          {/* Фильтрация и экспорт */}
          <h3 className={styles.sectionHeading} id="auto-order-filter">{t("expeditorGuidePage.autoOrder.filterTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoOrder.filterDesc")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoOrder.exportDesc")}</p>

          {/* Статусы */}
          <h3 className={styles.sectionHeading} id="auto-order-statuses">{t("expeditorGuidePage.autoOrder.statusesTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoOrder.statusesDesc")}</p>

          <div className={styles.statusFlow}>
            {[
              { color: "orange", key: "status1" },
              { color: "globe", key: "status2" },
              { color: "pink", key: "status3" },
              { color: "gray", key: "status4" },
              { color: "blue", key: "status5" },
              { color: "purple", key: "status6" },
              { color: "purple", key: "status7" },
              { color: "green", key: "status8" },
              { color: "blue", key: "status9" },
              { color: "blue", key: "status10" },
              { color: "orange", key: "status11" },
              { color: "green", key: "status12", last: true },
            ].map((s) => (
              <div className={styles.statusItem} key={s.key}>
                <div className={styles.statusLine}>
                  <div className={`${styles.statusDot} ${styles[s.color]}`}></div>
                  {!s.last && <div className={styles.statusConnector}></div>}
                </div>
                <div className={styles.statusContent}>
                  <h4>{t(`expeditorGuidePage.autoOrder.${s.key}`)}</h4>
                  <p>{t(`expeditorGuidePage.autoOrder.${s.key}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* История */}
          <h3 className={styles.sectionHeading} id="auto-order-history">{t("expeditorGuidePage.autoOrder.historyTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoOrder.historyDesc")}</p>
          <div className={styles.cardsGrid} style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#DBEAFE" }}>A</div>
              <h4>{t("expeditorGuidePage.autoOrder.geoTracking")}</h4>
              <p>{t("expeditorGuidePage.autoOrder.geoTrackingDesc")}</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#D1FAE5" }}>B</div>
              <h4>{t("expeditorGuidePage.autoOrder.transportDocs")}</h4>
              <p>{t("expeditorGuidePage.autoOrder.transportDocsDesc")}</p>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon} style={{ background: "#FEF3C7" }}>C</div>
              <h4>{t("expeditorGuidePage.autoOrder.driverDocs")}</h4>
              <p>{t("expeditorGuidePage.autoOrder.driverDocsDesc")}</p>
            </div>
          </div>
        </div>

        {/* ==================== AUTO: АВТОПАРК ==================== */}
        <div className={sectionClass("auto-fleet")} id="auto-fleet">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("expeditorGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.autoFleet.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.autoFleet.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoFleet.howToAdd")}</h3>
          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.autoFleet.step${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.autoFleet.step${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoFleet.capabilities")}</h3>
          {["accounting", "documents"].map((key) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`expeditorGuidePage.autoFleet.${key}`)}</h4>
                <p>{t(`expeditorGuidePage.autoFleet.${key}Desc`)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ==================== AUTO: ВОДИТЕЛИ ==================== */}
        <div className={sectionClass("auto-drivers")} id="auto-drivers">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("expeditorGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.autoDrivers.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.autoDrivers.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoDrivers.howToAdd")}</h3>
          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.autoDrivers.step${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.autoDrivers.step${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoDrivers.capabilities")}</h3>
          {["registration", "verification", "control"].map((key) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`expeditorGuidePage.autoDrivers.${key}`)}</h4>
                <p>{t(`expeditorGuidePage.autoDrivers.${key}Desc`)}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ==================== AUTO: МАРКЕТПЛЕЙС ==================== */}
        <div className={sectionClass("auto-marketplace")} id="auto-marketplace">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("expeditorGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.autoMarketplace.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.autoMarketplace.subtitle")}</p>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("expeditorGuidePage.autoMarketplace.callout")}</div>
          </div>

          <p className={styles.bodyText}>{t("expeditorGuidePage.autoMarketplace.tabsDesc")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoMarketplace.companiesTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoMarketplace.companiesDesc")}</p>

          {["orgData", "experience", "geography", "directContact", "invitation"].map((key) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`expeditorGuidePage.autoMarketplace.${key}`)}</h4>
                <p>{t(`expeditorGuidePage.autoMarketplace.${key}Desc`)}</p>
              </div>
            </div>
          ))}

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoMarketplace.candidatesTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoMarketplace.candidatesDesc")}</p>

          <table className={styles.tbl}>
            <thead>
              <tr>
                <th>{t("expeditorGuidePage.autoMarketplace.tableStatus")}</th>
                <th>{t("expeditorGuidePage.autoMarketplace.tableDescription")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className={`${styles.tag} ${styles.orange}`}>{t("expeditorGuidePage.autoMarketplace.statusPending")}</span></td>
                <td>{t("expeditorGuidePage.autoMarketplace.statusPendingDesc")}</td>
              </tr>
              <tr>
                <td><span className={`${styles.tag} ${styles.green}`}>{t("expeditorGuidePage.autoMarketplace.statusAccepted")}</span></td>
                <td>{t("expeditorGuidePage.autoMarketplace.statusAcceptedDesc")}</td>
              </tr>
              <tr>
                <td><span className={`${styles.tag} ${styles.blue}`}>{t("expeditorGuidePage.autoMarketplace.statusRejected")}</span></td>
                <td>{t("expeditorGuidePage.autoMarketplace.statusRejectedDesc")}</td>
              </tr>
            </tbody>
          </table>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoMarketplace.publicOrdersTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoMarketplace.publicOrdersDesc")}</p>

          <div className={`${styles.callout} ${styles.success}`}>
            <div className={styles.calloutIcon}>✓</div>
            <div>{t("expeditorGuidePage.autoMarketplace.publicOrdersCallout")}</div>
          </div>
        </div>

        {/* ==================== AUTO: АНАЛИТИКА ==================== */}
        <div className={sectionClass("auto-analytics")} id="auto-analytics">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("expeditorGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.autoAnalytics.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.autoAnalytics.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoAnalytics.mainIndicators")}</h3>
          {["orderStats", "avgCost", "totalMileage", "historyDynamics", "top10", "onTime", "shipmentsCount"].map((key) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`expeditorGuidePage.autoAnalytics.${key}`)}</h4>
                <p>{t(`expeditorGuidePage.autoAnalytics.${key}Desc`)}</p>
              </div>
            </div>
          ))}

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoAnalytics.tenderAnalysis")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.autoAnalytics.tenderAnalysisDesc")}</p>
        </div>

        {/* ==================== AUTO: СОТРУДНИКИ ==================== */}
        <div className={sectionClass("auto-staff")} id="auto-staff">
          <div className={`${styles.modeBadge} ${styles.auto}`}>{t("expeditorGuidePage.nav.auto")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.autoStaff.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.autoStaff.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.autoStaff.howToAdd")}</h3>
          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.autoStaff.step${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.autoStaff.step${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ============================================================ */}
        {/* АВИА РАЗДЕЛЫ */}
        {/* ============================================================ */}

        {/* ==================== AVIA: ИСПОЛНЕНИЕ ЗАКАЗОВ ==================== */}
        <div className={sectionClass("avia-execute")} id="avia-execute">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("expeditorGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.aviaExecute.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.aviaExecute.subtitle")}</p>

          {/* Тендер */}
          <h3 className={styles.sectionHeading} id="avia-execute-tender">{t("expeditorGuidePage.aviaExecute.tenderTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaExecute.tenderDesc")}</p>

          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.aviaExecute.tenderStep${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.aviaExecute.tenderStep${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Прямое назначение */}
          <h3 className={styles.sectionHeading} id="avia-execute-direct">{t("expeditorGuidePage.aviaExecute.directTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaExecute.directDesc")}</p>

          <div className={styles.stepList}>
            {[1, 2].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.aviaExecute.directStep${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.aviaExecute.directStep${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Публичные заказы */}
          {/* <h3 className={styles.sectionHeading} id="avia-execute-public">{t("expeditorGuidePage.aviaExecute.publicTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaExecute.publicDesc")}</p>

          <div className={styles.stepList}>
            {[1, 2, 3].map((i) => (
              <div className={styles.stepItem} key={i}>
                <div className={styles.stepBadge}>{i}</div>
                <div className={styles.stepContent}>
                  <h4>{t(`expeditorGuidePage.aviaExecute.publicStep${i}`)}</h4>
                  <p>{t(`expeditorGuidePage.aviaExecute.publicStep${i}Desc`)}</p>
                </div>
              </div>
            ))}
          </div> */}
        </div>

        {/* ==================== AVIA: РАЗДЕЛ ЗАКАЗЫ ==================== */}
        <div className={sectionClass("avia-order")} id="avia-order">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("expeditorGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.aviaOrder.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.aviaOrder.subtitle")}</p>

          <h3 className={styles.sectionHeading} id="avia-order-statuses">{t("expeditorGuidePage.aviaOrder.statusesTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaOrder.statusesDesc")}</p>

          <div className={styles.statusFlow}>
            {[
              { color: "orange", key: "status1" },
              { color: "pink", key: "status2" },
              { color: "blue", key: "status3" },
              { color: "green", key: "status4" },
              { color: "orange", key: "status5" },
              { color: "green", key: "status6", last: true },
            ].map((s) => (
              <div className={styles.statusItem} key={s.key}>
                <div className={styles.statusLine}>
                  <div className={`${styles.statusDot} ${styles[s.color]}`}></div>
                  {!s.last && <div className={styles.statusConnector}></div>}
                </div>
                <div className={styles.statusContent}>
                  <h4>{t(`expeditorGuidePage.aviaOrder.${s.key}`)}</h4>
                  <p>{t(`expeditorGuidePage.aviaOrder.${s.key}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ==================== AVIA: МАРКЕТПЛЕЙС ==================== */}
        <div className={sectionClass("avia-marketplace")} id="avia-marketplace">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("expeditorGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.aviaMarketplace.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.aviaMarketplace.subtitle")}</p>

          <div className={`${styles.callout} ${styles.info}`}>
            <div className={styles.calloutIcon}>i</div>
            <div>{t("expeditorGuidePage.aviaMarketplace.callout")}</div>
          </div>

          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaMarketplace.tabsDesc")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.aviaMarketplace.companiesTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaMarketplace.companiesDesc")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.aviaMarketplace.candidatesTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaMarketplace.candidatesDesc")}</p>

          <table className={styles.tbl}>
            <thead>
              <tr>
                <th>{t("expeditorGuidePage.autoMarketplace.tableStatus")}</th>
                <th>{t("expeditorGuidePage.autoMarketplace.tableDescription")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className={`${styles.tag} ${styles.orange}`}>{t("expeditorGuidePage.aviaMarketplace.statusPending")}</span></td>
                <td>{t("expeditorGuidePage.aviaMarketplace.statusPendingDesc")}</td>
              </tr>
              <tr>
                <td><span className={`${styles.tag} ${styles.green}`}>{t("expeditorGuidePage.aviaMarketplace.statusAccepted")}</span></td>
                <td>{t("expeditorGuidePage.aviaMarketplace.statusAcceptedDesc")}</td>
              </tr>
              <tr>
                <td><span className={`${styles.tag} ${styles.blue}`}>{t("expeditorGuidePage.aviaMarketplace.statusRejected")}</span></td>
                <td>{t("expeditorGuidePage.aviaMarketplace.statusRejectedDesc")}</td>
              </tr>
            </tbody>
          </table>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.aviaMarketplace.publicOrdersTitle")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaMarketplace.publicOrdersDesc")}</p>
        </div>

        {/* ==================== AVIA: АНАЛИТИКА ==================== */}
        <div className={sectionClass("avia-analytics")} id="avia-analytics">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("expeditorGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.aviaAnalytics.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.aviaAnalytics.subtitle")}</p>

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.aviaAnalytics.mainIndicators")}</h3>
          {["orderStats", "avgCost", "historyDynamics", "top10", "onTime", "shipmentsCount"].map((key) => (
            <div className={styles.featureRow} key={key}>
              <div className={styles.frIcon}>✓</div>
              <div>
                <h4>{t(`expeditorGuidePage.aviaAnalytics.${key}`)}</h4>
                <p>{t(`expeditorGuidePage.aviaAnalytics.${key}Desc`)}</p>
              </div>
            </div>
          ))}

          <h3 className={styles.sectionHeading}>{t("expeditorGuidePage.aviaAnalytics.tenderAnalysis")}</h3>
          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaAnalytics.tenderAnalysisDesc")}</p>
        </div>

        {/* ==================== AVIA: СОТРУДНИКИ ==================== */}
        <div className={sectionClass("avia-staff")} id="avia-staff">
          <div className={`${styles.modeBadge} ${styles.avia}`}>{t("expeditorGuidePage.nav.avia")}</div>
          <h1 className={styles.pageTitle}>{t("expeditorGuidePage.aviaStaff.title")}</h1>
          <p className={styles.pageSubtitle}>{t("expeditorGuidePage.aviaStaff.subtitle")}</p>
          <p className={styles.bodyText}>{t("expeditorGuidePage.aviaStaff.description")}</p>
        </div>
      </main>

      {/* FOOTER */}
      <div className={styles.footer}>
        <p>
          {t("expeditorGuidePage.footer.text")}{" "}
          <a href="mailto:product@biny.co">{t("expeditorGuidePage.footer.email")}</a>
        </p>
      </div>
    </div>
  );
};

export default ExpeditorGuidePage;
